import Bluebird from "bluebird";
import {
  is,
  object,
  number,
  string,
  date,
  array,
  size,
  nullable,
  defaulted,
  literal
} from "superstruct";
import { Document, Page, Session } from "@/tools/msa/models";
import { analyzeDocument } from "@/tools/msa/processing/analysis";
import { tokeniseDocument } from "@/tools/msa/processing/tokenisation";
import { encodeDocumentWithSnomed } from "@/tools/msa/processing/snomed";
import { calculateFileChecksum } from "@/tools/msa/processing/extraction/legacy";
import { encodeDocumentWithCustomTerms } from "@/tools/msa/processing/custom";

const SessionSave = object({
  version: literal(1),
  checksum: string(),
  numberOfPages: number(),
  session: object({
    fileId: string(),
    patient: object({
      name: string(),
      dateOfBirth: nullable(date())
    }),
    documents: array(
      object({
        date: nullable(string()),
        providerName: string(),
        practice: string(),
        chiefComplaint: string(),
        diagnoses: string(),
        assessments: string(),
        medications: string(),
        plans: string(),
        pages: size(array(number()), 2)
      })
    )
  })
});

type SessionSaveType = typeof SessionSave.TYPE;

const extractPagesToTuples = (pages: Page[]) => [
  pages[0].number,
  pages[pages.length - 1].number + 1
];

export const load = (file: File, session: Session): Promise<Session> =>
  Promise.all<SessionSaveType, string, Page[]>([
    file
      .text()
      .then(data =>
        JSON.parse(data, (name, value) => {
          if (
            typeof value === "string" &&
            /^\d\d\d\d-\d\d-\d\dT\d\d:\d\d:\d\d.\d\d\dZ$/.test(value)
          ) {
            const date = new Date(value);
            if (!Number.isNaN(date.getTime())) return date;
          }
          return value;
        })
      )
      .catch(() => Promise.reject({ type: "INVALID_FORMAT " }))
      .then(data => ({ version: 1, ...data }))
      .then(data =>
        is(data, SessionSave)
          ? data
          : Promise.reject({ type: "INVALID_SCHEMA" })
      ),
    calculateFileChecksum(session.file),
    Promise.resolve(
      session.documents
        .flatMap(document => document.pages)
        .sort((first, second) => first.number - second.number)
        .reduce<(Page | undefined)[]>(
          (array, page) => ((array[page.number - 1] = page), array),
          []
        )
    ).then(result =>
      result.includes(undefined)
        ? Promise.reject({ type: "INVALID_PAGES" })
        : (result as Page[])
    )
  ])
    .then(([loaded, checksum, pages]) =>
      loaded.checksum === checksum
        ? ([loaded, pages] as const)
        : Promise.reject({ type: "DIFFERENT_CHECKSUM" })
    )
    .then(([loaded, pages]) =>
      loaded.numberOfPages === pages.length
        ? ([loaded, pages] as const)
        : Promise.reject({ type: "DIFFERENT_PAGES" })
    )
    .then(
      ([loaded, pages]): Session => ({
        id: session.id,
        file: session.file,
        fileId: loaded.session.fileId,
        patient: loaded.session.patient,
        documents: loaded.session.documents.map(
          (document): Document => {
            console.log(pages, document.pages);
            return {
              tokens: [],
              items: [],
              dateOfBirth: null,
              date: document.date,
              chiefComplaint: document.chiefComplaint,
              providerName: document.providerName,
              practice: document.practice,
              diagnoses: document.diagnoses,
              assessments: document.assessments,
              medications: document.medications,
              plans: document.plans,
              pages: pages.slice(...document.pages).map(page => ({
                text: page.text,
                number: page.number,
                sections: [],
                tokens: [],
                date: null
              }))
            };
          }
        )
      })
    )
    .then(session =>
      Bluebird.resolve(session.documents)
        .map(document => tokeniseDocument(document, session), {
          concurrency: 6
        })
        .map(document => analyzeDocument(document, session), { concurrency: 6 })
        .map(document => encodeDocumentWithSnomed(document, session), {
          concurrency: 6
        })
        .map(document => encodeDocumentWithCustomTerms(document, session), {
          concurrency: 6
        })
        .then(documents => ({
          ...session,
          documents
        }))
    );

export const save = (session: Session): Promise<string> =>
  calculateFileChecksum(session.file)
    .then(
      (checksum): SessionSaveType => ({
        version: 1,
        checksum,
        numberOfPages: session.documents.flatMap(document => document.pages)
          .length,
        session: {
          fileId: session.fileId,
          patient: {
            name: session.patient.name,
            dateOfBirth: session.patient.dateOfBirth
          },
          documents: session.documents.map(document => ({
            date: document.date,
            providerName: document.providerName,
            practice: document.practice,
            chiefComplaint: document.chiefComplaint,
            diagnoses: document.diagnoses,
            assessments: document.assessments,
            medications: document.medications,
            plans: document.plans,
            pages: extractPagesToTuples(document.pages)
          }))
        }
      })
    )
    .then(data => JSON.stringify(data));

export const download = (data: string, fileName: string = "output") => {
  const anchor: HTMLAnchorElement = document.createElement("a");
  anchor.download = `${fileName}.vrbmsa`;
  anchor.rel = "noopener";
  anchor.href = URL.createObjectURL(
    new Blob([data], { type: "application/json" })
  );
  anchor.dispatchEvent(new MouseEvent("click"));
  setTimeout(() => URL.revokeObjectURL(anchor.href), 150);
};
